import React, { useState, useEffect, useCallback } from "react";
import MetaTags from "react-meta-tags";
import { useHistory, useParams } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Button,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Badge,
  Table
} from "reactstrap";
import { BILLING_URL, PAYMENT_URL, VOUCHER_URL, ACCOUNTS_SUMMARY_URL } from "helpers/url_helper";
import { get } from "helpers/api_helper";
import moment from "moment";
import { useTranslation } from "react-i18next";

// Import Financial Sub-modules
import ManageBill from "./ManageAccounts/ManageBill";
import ManagePayment from "./ManageAccounts/ManagePayment";
import ManagePettyCash from "./ManageAccounts/ManagePettyCash";
import ManageBankDeposit from "./ManageAccounts/ManageBankDeposit";
import ManagePassbook from "./ManageAccounts/ManagePassbook";

// Icons
import {
  RiBillLine,
  RiHandCoinLine,
  RiWallet3Line,
  RiBankLine,
  RiBookOpenLine,
  RiArrowUpLine,
  RiArrowDownLine,
  RiPrinterLine,
  RiMoneyDollarCircleLine,
  RiShieldCheckLine,
  RiCalendarEventLine
} from "react-icons/ri";

import "./accounts.scss";

const Accounts = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { tab } = useParams();

  const normalizeTab = (tStr) => {
    if (!tStr) return "bills";
    const lower = tStr.toLowerCase();
    if (lower === "pettycash" || lower === "petty-cash") return "petty-cash";
    if (lower === "bank" || lower === "bank-deposit") return "bank-deposit";
    return lower;
  };

  const [activeTab, setActiveTab] = useState(normalizeTab(tab));
  const [timeFilter, setTimeFilter] = useState("all"); // 'all', 'today', 'month'

  useEffect(() => {
    if (tab) {
      setActiveTab(normalizeTab(tab));
    }
  }, [tab]);

  const handleTabChange = (newTab) => {
    const norm = normalizeTab(newTab);
    setActiveTab(norm);
    history.push(`/accounts/${norm}`);
  };

  // Financial Summary State
  const [metrics, setMetrics] = useState({
    totalInvoiced: 0,
    totalCollected: 0,
    totalPending: 0,
    totalExpenses: 0,
    netCashflow: 0,
    invoicesCount: 0,
    paymentsCount: 0
  });

  const [loading, setLoading] = useState(false);

  const extractList = (res) => {
    if (!res) return [];
    if (Array.isArray(res)) return res;
    if (Array.isArray(res.items)) return res.items;
    if (Array.isArray(res.body?.items)) return res.body.items;
    if (Array.isArray(res.body)) return res.body;
    if (Array.isArray(res.data?.items)) return res.data.items;
    if (Array.isArray(res.data)) return res.data;
    return [];
  };

  const filterByTime = (items, dateFieldNames = ['date', 'datetime', 'created_at']) => {
    if (!timeFilter || timeFilter === 'all') return items;
    return items.filter(item => {
      let rawDate = null;
      for (const f of dateFieldNames) {
        if (item[f]) { rawDate = item[f]; break; }
      }
      if (!rawDate) return true;
      const m = moment(rawDate);
      if (timeFilter === 'today') return m.isSame(moment(), 'day');
      if (timeFilter === 'month') return m.isSame(moment(), 'month');
      return true;
    });
  };

  const fetchFinancialSummary = useCallback(async () => {
    setLoading(true);
    try {
      // 1. Try dedicated summary endpoint first
      try {
        const summaryRes = await get(`${ACCOUNTS_SUMMARY_URL}?timeFilter=${timeFilter}`);
        const data = summaryRes?.body || summaryRes?.data || summaryRes;
        if (data && data.success) {
          setMetrics({
            totalInvoiced: parseFloat(data.totalInvoiced || 0),
            totalCollected: parseFloat(data.totalCollected || 0),
            totalPending: parseFloat(data.totalPending || 0),
            totalExpenses: parseFloat(data.totalExpenses || 0),
            netCashflow: parseFloat(data.netCashflow || 0),
            invoicesCount: parseInt(data.invoicesCount || 0, 10),
            paymentsCount: parseInt(data.paymentsCount || 0, 10)
          });
          setLoading(false);
          return;
        }
      } catch (err) {
        console.warn("Dedicated summary route failed, using parallel fallback:", err);
      }

      // 2. Parallel fallback
      const [billRes, payRes, pettyRes] = await Promise.allSettled([
        get(`${BILLING_URL}?limit=1000`),
        get(`${PAYMENT_URL}?limit=1000`),
        get(`${VOUCHER_URL}?limit=1000`)
      ]);

      const billData = billRes.status === 'fulfilled' ? billRes.value : null;
      const payData = payRes.status === 'fulfilled' ? payRes.value : null;
      const pettyData = pettyRes.status === 'fulfilled' ? pettyRes.value : null;

      const allBills = extractList(billData);
      const allPayments = extractList(payData);
      const allPetty = extractList(pettyData);

      const filteredBills = filterByTime(allBills, ['date', 'created_at', 'datetime']);
      const filteredPayments = filterByTime(allPayments, ['payment_date', 'date', 'created_at', 'datetime']);
      const filteredPetty = filterByTime(allPetty, ['datetime', 'date', 'created_at']);

      const activeBills = filteredBills.filter(b => b.status !== 3 && b.status !== '3');
      const activePayments = filteredPayments.filter(p => p.status !== 1 && p.status !== '1');

      const totalBilled = activeBills.reduce((sum, b) => sum + (parseFloat(b.total || b.total_value || 0) || 0), 0);
      const totalPaidFromBills = activeBills.reduce((sum, b) => sum + (parseFloat(b.paid || 0) || 0), 0);
      const totalPaidFromPayments = activePayments.reduce((sum, p) => sum + (parseFloat(p.amount || 0) || 0), 0);
      const totalCollected = totalPaidFromBills > 0 ? totalPaidFromBills : totalPaidFromPayments;
      
      const totalPending = activeBills.reduce((sum, b) => sum + (parseFloat(b.pending || 0) || 0), 0);
      const totalExp = filteredPetty.reduce((sum, p) => sum + (parseFloat(p.total_value || p.amount || 0) || 0), 0);

      setMetrics({
        totalInvoiced: totalBilled,
        totalCollected: totalCollected,
        totalPending: totalPending,
        totalExpenses: totalExp,
        netCashflow: totalCollected - totalExp,
        invoicesCount: activeBills.length,
        paymentsCount: activePayments.length || activeBills.filter(b => parseFloat(b.paid || 0) > 0).length
      });
    } catch (e) {
      console.error("Error loading account metrics:", e);
    } finally {
      setLoading(false);
    }
  }, [timeFilter]);

  useEffect(() => {
    fetchFinancialSummary();
  }, [fetchFinancialSummary, activeTab]);

  const collectionRate = metrics.totalInvoiced > 0
    ? Math.round((metrics.totalCollected / metrics.totalInvoiced) * 100)
    : (metrics.totalCollected > 0 ? 100 : 0);

  return (
    <React.Fragment>
      <div className="page-content accounts-page-wrapper">
        <MetaTags>
          <title>{t("Accounts & Billing")} | 909 Dental OPD</title>
        </MetaTags>

        <Container fluid className="accounts-container-custom px-3 px-lg-4">
          {/* Header Bar */}
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
            <div>
              <h4 className="font-weight-bold text-dark font-size-20 mb-1">
                {t("Accounts & Billing")}
              </h4>
              <span className="text-muted font-size-12">
                {t("Invoices")}, {t("Collections Received")}, {t("Clinic Expenses")} &amp; {t("Reports")}.
              </span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <div className="btn-group bg-white rounded-pill p-1 border shadow-xs gap-2">
                <button
                  type="button"
                  className={`btn btn-xs rounded-pill px-3 py-1 font-size-11 ${timeFilter === 'all' ? 'btn-primary font-weight-bold shadow-xs' : 'btn-light text-muted'}`}
                  onClick={() => setTimeFilter('all')}
                >
                  {t("View all")}
                </button>
                <button
                  type="button"
                  className={`btn btn-xs rounded-pill px-3 py-1 font-size-11 ${timeFilter === 'month' ? 'btn-primary font-weight-bold shadow-xs' : 'btn-light text-muted'}`}
                  onClick={() => setTimeFilter('month')}
                >
                  {t("Default")}
                </button>
                <button
                  type="button"
                  className={`btn btn-xs rounded-pill px-3 py-1 font-size-11 ${timeFilter === 'today' ? 'btn-primary font-weight-bold shadow-xs' : 'btn-light text-muted'}`}
                  onClick={() => setTimeFilter('today')}
                >
                  {t("New")}
                </button>
              </div>

              <Button
                color="light"
                size="sm"
                className="border rounded-pill px-3 font-size-12 d-inline-flex align-items-center gap-1 shadow-xs"
                onClick={() => window.print()}
              >
                <RiPrinterLine /> {t("Print Bill")}
              </Button>
            </div>
          </div>

          {/* Executive Dental Financial KPI Metrics Cards */}
          <Row className="g-3 mb-4">
            {/* 1. Total Invoiced */}
            <Col md={6} lg={3}>
              <div className="account-kpi-card">
                <div>
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <div className="kpi-icon-box bg-primary-subtle text-primary">
                      <RiBillLine />
                    </div>
                    <Badge color="light" className="text-primary border font-size-11">
                      {metrics.invoicesCount} {t("Invoices")}
                    </Badge>
                  </div>
                  <div className="kpi-label">{t("Total Invoiced")}</div>
                  <div className="kpi-value text-primary">
                    ₹{metrics.totalInvoiced.toLocaleString('en-IN')}
                  </div>
                </div>
                <div className="kpi-footer-badge text-muted border-top pt-2 mt-2">
                  <RiShieldCheckLine className="text-success" />
                  <span>{t("Estimated Cost")}</span>
                </div>
              </div>
            </Col>

            {/* 2. Total Collected */}
            <Col md={6} lg={3}>
              <div className="account-kpi-card">
                <div>
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <div className="kpi-icon-box bg-success-subtle text-success">
                      <RiHandCoinLine />
                    </div>
                    <Badge color="success" className="font-size-11">
                      {collectionRate}% {t("Completed")}
                    </Badge>
                  </div>
                  <div className="kpi-label">{t("Collections Received")}</div>
                  <div className="kpi-value text-success">
                    ₹{metrics.totalCollected.toLocaleString('en-IN')}
                  </div>
                </div>
                <div className="kpi-footer-badge text-success border-top pt-2 mt-2">
                  <RiArrowUpLine />
                  <span>{t("Payment Mode")}</span>
                </div>
              </div>
            </Col>

            {/* 3. Outstanding Patient Dues */}
            <Col md={6} lg={3}>
              <div className="account-kpi-card">
                <div>
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <div className="kpi-icon-box bg-warning-subtle text-warning">
                      <RiMoneyDollarCircleLine />
                    </div>
                    <Badge color="danger" className="font-size-11">
                      {t("Balance Due")}
                    </Badge>
                  </div>
                  <div className="kpi-label">{t("Outstanding Receivables")}</div>
                  <div className="kpi-value text-danger">
                    ₹{metrics.totalPending.toLocaleString('en-IN')}
                  </div>
                </div>
                <div className="kpi-footer-badge text-warning border-top pt-2 mt-2">
                  <RiCalendarEventLine />
                  <span>{t("Treatment Plan")}</span>
                </div>
              </div>
            </Col>

            {/* 4. Petty Cash & Expenses */}
            <Col md={6} lg={3}>
              <div className="account-kpi-card">
                <div>
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <div className="kpi-icon-box bg-info-subtle text-info">
                      <RiWallet3Line />
                    </div>
                    <Badge color="info" className="font-size-11">
                      {t("Procedure")}
                    </Badge>
                  </div>
                  <div className="kpi-label">{t("Clinic Expenses")}</div>
                  <div className="kpi-value text-dark">
                    ₹{metrics.totalExpenses.toLocaleString('en-IN')}
                  </div>
                </div>
                <div className="kpi-footer-badge text-muted border-top pt-2 mt-2">
                  <RiArrowDownLine className="text-danger" />
                  <span>{t("Net Amount")}</span>
                </div>
              </div>
            </Col>
          </Row>

          {/* Tabbed Financial Workspace Navigation */}
          <Nav className="accounts-nav-tabs">
            <NavItem>
              <NavLink
                className={`cursor-pointer ${activeTab === "bills" ? "active" : ""}`}
                onClick={() => handleTabChange("bills")}
                style={{ cursor: "pointer" }}
              >
                <RiBillLine className="font-size-16" /> {t("Invoices")}
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={`cursor-pointer ${activeTab === "payments" ? "active" : ""}`}
                onClick={() => handleTabChange("payments")}
                style={{ cursor: "pointer" }}
              >
                <RiHandCoinLine className="font-size-16" /> {t("Payment Receipts")}
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={`cursor-pointer ${activeTab === "pettycash" || activeTab === "petty-cash" ? "active" : ""}`}
                onClick={() => handleTabChange("pettycash")}
                style={{ cursor: "pointer" }}
              >
                <RiWallet3Line className="font-size-16" /> {t("Expenses & Petty Cash")}
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={`cursor-pointer ${activeTab === "bank" || activeTab === "bank-deposit" ? "active" : ""}`}
                onClick={() => handleTabChange("bank")}
                style={{ cursor: "pointer" }}
              >
                <RiBankLine className="font-size-16" /> {t("Bank Deposits")}
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={`cursor-pointer ${activeTab === "passbook" ? "active" : ""}`}
                onClick={() => handleTabChange("passbook")}
                style={{ cursor: "pointer" }}
              >
                <RiBookOpenLine className="font-size-16" /> {t("Ledger & Passbook")}
              </NavLink>
            </NavItem>
          </Nav>

          {/* Tab Content Body */}
          <div className="accounts-content-card">
            <TabContent activeTab={activeTab}>
              {/* Tab 1: Invoices & Billing */}
              <TabPane tabId="bills">
                <ManageBill />
              </TabPane>

              {/* Tab 2: Payments & Collections */}
              <TabPane tabId="payments">
                <ManagePayment />
              </TabPane>

              {/* Tab 3: Petty Cash */}
              <TabPane tabId="petty-cash">
                <ManagePettyCash />
              </TabPane>

              {/* Tab 4: Bank Deposits */}
              <TabPane tabId="bank-deposit">
                <ManageBankDeposit />
              </TabPane>

              {/* Tab 5: Practice Passbook & Ledger */}
              <TabPane tabId="passbook">
                <ManagePassbook />
              </TabPane>
            </TabContent>
          </div>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Accounts;
